import React from 'react';

import { Button, ButtonProps, getAttrToShowTooltip } from 'ui-kit';

import { UserRole } from '__generated__/globalTypes';
import { useHasAnyRole } from 'hooks/useHasRole';
import { NO_TAPE_ROLE_MESSAGE } from './errorMessages';

/** Whoever may upload a loan tape (api `getFilePostUrl`) may act on its Tapestry gates. */
export const TAPESTRY_GATE_ROLES: readonly UserRole[] = [
  UserRole.portfolio_manager,
  UserRole.deal_manager,
];

export const ROLE_GATE_TOOLTIP = NO_TAPE_ROLE_MESSAGE;

export interface RoleGatedButtonProps extends ButtonProps {
  /** Roles, any one of which enables the action. Defaults to the Tapestry gate roles. */
  allowedRoles?: readonly UserRole[];
}

/**
 * A `Button` that disables itself when the current user holds none of
 * `allowedRoles` and explains why via a tooltip. The api enforces the same role
 * (`@requireRoles` → `E-4030`); this is the matching client-side affordance so
 * the action doesn't look available-but-broken.
 *
 * The tooltip is attached to a wrapper span, not the button: a disabled
 * `<button>` does not emit pointer events, so a tooltip bound to it never
 * shows. It appears ONLY for the missing-role case — callers that pass their
 * own `disabled` (e.g. a not-yet-ready gate) keep their own messaging with no
 * permission tooltip.
 */
export const RoleGatedButton: React.FC<RoleGatedButtonProps> = ({
  allowedRoles = TAPESTRY_GATE_ROLES,
  disabled,
  ...buttonProps
}) => {
  const { hasRole, loading } = useHasAnyRole(allowedRoles);

  // Disable (without a tooltip) while permissions resolve so the button can't
  // be clicked before we know the role; show the permission tooltip only once
  // we've confirmed the role is missing.
  const blockedByRole = !loading && !hasRole;
  // Only surface the permission tooltip when the missing role is the *sole*
  // reason the button is disabled. If the caller passed `disabled` for its own
  // reason (e.g. a gate that isn't ready, required fields unmapped), that state
  // keeps its own messaging and we must not show the role tooltip.
  const showRoleTooltip = blockedByRole && !disabled;
  const tooltipAttrs = showRoleTooltip ? getAttrToShowTooltip({ content: ROLE_GATE_TOOLTIP }) : {};

  return (
    <span {...tooltipAttrs} className="inline-block">
      <Button {...buttonProps} disabled={disabled || loading || blockedByRole} />
    </span>
  );
};
